<template>
  <OverlayFrame
    :zIndex="overlayZIndex"
    title="Travel"
    :subtitle="subtitle"
    :icon="ICONS.actions.travel"
    closeLabel="Close travel prompt"
    @close="emit('close')"
  >
    <div class="stack tight">
      <UiCallout v-if="disabledReason" variant="warning">{{ disabledReason }}</UiCallout>

      <div class="travel-meta">
        <div>
          <div class="label text-muted">Destination</div>
          <div class="value">{{ destinationName }}</div>
        </div>
        <div>
          <div class="label text-muted">ETA</div>
          <div class="value stat-mono num">{{ formatMs(durationMs) }}</div>
        </div>
      </div>

      <div class="actions">
        <UiButton size="sm" variant="secondary" @click="emit('close')">Stay</UiButton>
        <UiButton size="sm" tone="travel" :disabled="disabled" @click="confirmTravel">
          Walk
        </UiButton>
      </div>
    </div>
  </OverlayFrame>
</template>

<script setup lang="ts">
import { computed } from "vue";
import OverlayFrame from "../../../components/overlay/OverlayFrame.vue";
import UiButton from "../../../components/ui/UiButton.vue";
import UiCallout from "../../../components/ui/UiCallout.vue";
import { ICONS } from "../../../lib/icons";
import { formatMs } from "../../../lib/format";

const props = withDefaults(
  defineProps<{
    overlayZIndex?: number;
    destinationName: string;
    durationMs: number;
    disabled?: boolean;
    disabledReason?: string | null;
    onConfirm?: () => Promise<void> | void;
    /** If false, keep the overlay open after confirm (caller can update/close it). */
    closeAfterConfirm?: boolean;
  }>(),
  {
    overlayZIndex: 4000,
    disabled: false,
    disabledReason: null,
    onConfirm: undefined,
    closeAfterConfirm: true
  }
);

const emit = defineEmits<{ close: [] }>();

const subtitle = computed(() => `Going to ${props.destinationName}`);

async function confirmTravel() {
  if (props.disabled) return;
  await props.onConfirm?.();
  if (props.closeAfterConfirm) {
    emit("close");
  }
}
</script>

<style scoped>
.travel-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.8rem;
  margin-top: 0.25rem;
}

.label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.value {
  margin-top: 0.2rem;
  font-weight: 650;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-top: 0.25rem;
}

@media (max-width: 520px) {
  .travel-meta {
    grid-template-columns: 1fr;
  }
}
</style>

